import { z } from 'zod';
import { UserSpaceSchema } from './spaces.js';
/**
 * Контракты эндпоинтов `modules/auth` (см. `docs/modules/auth.md §3.4`).
 * Используются и бэкендом (validation), и фронтендом (типизация клиента).
 */
// ─── Политика паролей ────────────────────────────────────────────────────
//
// Минимальная по SRS: длина >=10 символов, не совпадает с логином.
// Всё остальное (expiration, history, common-passwords list) сознательно
// не включено — internal tool на ~70 пользователей (auth.md §3.3).
export const PASSWORD_MIN_LENGTH = 10;
/**
 * Базовая проверка пароля как строки — без контекста логина.
 * Проверка «пароль не равен логину» делается отдельной функцией, потому
 * что Zod не видит соседние поля без `.refine` на объекте.
 */
export const PasswordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, {
    message: `Пароль должен содержать не менее ${PASSWORD_MIN_LENGTH} символов`,
})
    .max(256, { message: 'Пароль слишком длинный' });
export const LoginSchema = z
    .string()
    .trim()
    .min(1, { message: 'Укажите логин' })
    .max(64, { message: 'Логин слишком длинный' });
// ─── POST /api/auth/login ────────────────────────────────────────────────
export const LoginRequestSchema = z.object({
    login: LoginSchema,
    // На login-эндпоинте не валидируем длину пароля: неподходящую длину
    // сервер должен трактовать как обычную неверную пару (invalid_credentials),
    // чтобы не раскрывать структуру политики паролей.
    password: z.string().min(1).max(256),
});
// ─── Данные текущего пользователя ────────────────────────────────────────
//
// Это DTO, который возвращают и `/api/auth/login`, и `/api/auth/me`.
// password_hash, is_active и прочие служебные поля НЕ попадают на фронт.
export const CurrentUserSchema = z.object({
    id: z.string().uuid(),
    login: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    isPlatformAdmin: z.boolean(),
    hasJiraToken: z.boolean().default(false),
    hasLlmApiKey: z.boolean().default(false),
    avatarUrl: z.string().nullable().default(null),
    /** Как входит: корпоративный SSO (пароль в Lens не нужен) или пароль Lens. */
    authMethod: z.enum(['sso', 'password']).default('password'),
});
export const LoginResponseSchema = z.object({
    user: CurrentUserSchema,
});
// ─── GET /api/auth/me ────────────────────────────────────────────────────
//
// Возвращает такого же `user`, плюс `spaces` — список пространств, где
// пользователь является участником (UserSpace, минимальный slice для
// топбара/бокового меню). В M1 это поле всегда было пустым; в M2 API
// начинает возвращать реальные данные.
export const MeResponseSchema = z.object({
    user: CurrentUserSchema,
    spaces: z.array(UserSpaceSchema).default([]),
});
// ─── PUT /api/auth/me/profile ────────────────────────────────────────────
export const UpdateProfileRequestSchema = z.object({
    displayName: z.string().trim().min(1).max(120),
});
// ─── POST /api/auth/password ─────────────────────────────────────────────
export const PasswordChangeRequestSchema = z.object({
    oldPassword: z.string().min(1).max(256),
    newPassword: PasswordSchema,
});
// ─── GET /api/auth/csrf ──────────────────────────────────────────────────
export const CsrfTokenResponseSchema = z.object({
    token: z.string().min(1),
});
// ─── Error envelope ──────────────────────────────────────────────────────
//
// Фронт смотрит на `error.code` для локализации, поэтому коды — часть
// контракта. Список соответствует login flow из auth.md §3.5.
export const AUTH_ERROR_CODES = [
    'validation_error',
    'invalid_credentials',
    'use_sso',
    'unauthorized',
    'rate_limited',
    'csrf_invalid',
    'password_policy_violation',
    'password_equals_login',
];
